import React, { useEffect, useMemo, useState } from 'react';
import { StyledButton } from '../styles/emailIntegration.styles';
import { EMAIL_PROVIDERS } from './emailProviders';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import { VscLoading } from 'react-icons/vsc';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { emailIntegrationService } from '@/_services/api/emailIntegration';
import { GoToChannel } from '@/_elements/Links';
import DynamicChannelSelectFilter from '@/components/Admin/DynamicChannelSelectFilter';

const EmailProviderCard = ({ user = {}, teams, account, fetchAccounts }) => {
    const [isBusy, setIsBusy] = useState(false);
    const provider = EMAIL_PROVIDERS[account.emailProvider];
    const Icon = provider?.icon;
    const [selectedChannel, setSelectedChannel] = useState(null);
    // teamIds={(teams || []).map((t) => t._id)}

    const teamIds = useMemo(() => {
        if (user && user?.role === 'admin') {
            return (teams || []).map((t) => t._id);
        }
        return (user?.teamList || [])
            .filter(({ role }) => role === 'teamManager' || role === 'owner')
            .map(({ team }) => team?._id || team?.id || team)
            .filter(Boolean);
    }, [user, teams]);

    useEffect(() => {
        if (account?.chatroom && account?.chatroom?._id) {
            setSelectedChannel({ value: account.chatroom._id, label: account.chatroom?.title });
        } else {
            setSelectedChannel(null);
        }
    }, [account?.chatroom]);

    const handleSaveChannel = async (accountId) => {
        const selected = selectedChannel?.value || null;
        try {
            setIsBusy(true);
            const response = await emailIntegrationService.updateEmailChannel(accountId, {
                chatroom: selected,
            });
            if (!response?.chatroom) {
                toast.success('Channel removed');
            } else {
                toast.success('Channel saved');
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to update channel');
        } finally {
            fetchAccounts();
            setIsBusy(false);
        }
    };

    const handleDisconnect = async (accountId) => {
        try {
            setIsBusy(true);
            await emailIntegrationService.disconnectEmail({ accountId });
            toast.success('Gmail account disconnected');
            fetchAccounts();
        } catch (error) {
            toast.error(error?.message || 'Failed to disconnect Gmail account');
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <div className="d-flex flex-column border   mb-4  rounded">
            <div className="d-flex flex-row justify-content-between p-3 pb-0 ">
                <div className="d-flex flex-row">
                    <div>{Icon}</div>
                    <div className="d-flex flex-column ml-4">
                        <div>{provider?.name}</div>
                        <div className="text-muted">{account?.email}</div>
                        <div>
                            {account.lastSyncedAt && (
                                <p className="text-muted small mb-2">
                                    Last synched:&nbsp;
                                    {formatDistanceToNow(new Date(account.lastSyncedAt), { addSuffix: true })}
                                </p>
                            )}
                        </div>
                        <hr />
                    </div>
                </div>
                <div>
                    <span className={`badge ${account?.archived ? 'badge-danger' : 'badge-success'}`}>
                        {account?.archived ? 'Disconnected' : 'Connected'}
                    </span>
                </div>
            </div>
            <div className="p-3">
                <span className="text-muted mb-2">Copy incoming email to channel:</span>
                <div className="d-flex flex-row">
                    <div className="flex-grow-1">
                        <DynamicChannelSelectFilter
                            onRoomSelect={(option) => setSelectedChannel(option)}
                            selectedOption={selectedChannel}
                            placeholder="Select Channel"
                            teamIds={teamIds}
                            permissionRule="get:teamSettings"
                        />
                    </div>
                    <div className="mt-2">
                        {selectedChannel && selectedChannel?.value && (
                            <GoToChannel channel={{ ...selectedChannel, title: '', label: '' }} />
                        )}
                    </div>
                    <div className="ml-2">
                        <Button variant="primary" onClick={() => handleSaveChannel(account._id)} disabled={isBusy}>
                            {isBusy ? 'Saving...' : 'Save '}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="card-footer d-flex justify-content-between mt-2">
                <div></div>
                <StyledButton variant="outline-danger" onClick={() => handleDisconnect(account._id)} disabled={isBusy}>
                    Disconnect
                    {isBusy && <VscLoading className="spin ml-2" />}
                </StyledButton>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.user.user,
    teams: state.teams.teams,
});

export default connect(mapStateToProps)(EmailProviderCard);
